import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, RotateCcw, Voicemail as VoicemailIcon } from 'lucide-react';
import Button from '../ui/Button';
import AudioPlayback from './AudioPlayback';
import salesService from '../../services/sales';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz } from '../../utils/timezone';
import { MATCH_KIND_LABEL, callLength, isOverdue } from '../../utils/calls';
import type { SalesPerson, Voicemail } from '../../types/sales';

/**
 * One voicemail: who left it, for which mailbox, its transcript and audio
 * (for a Sales Admin, the mailbox owner or whoever it is assigned to), and
 * its follow-up: give it to someone, mark it followed up with a note, or
 * open it again.
 */

export default function VoicemailCard({ vm, people, tz, onChange, linkCustomer = true }: {
  vm: Voicemail; people: SalesPerson[]; tz: string; onChange: (vm: Voicemail) => void; linkCustomer?: boolean;
}) {
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const who = vm.matched_name || vm.caller_name || vm.caller_number || 'Unknown number';
  const length = callLength(vm.length_sec);
  const overdue = isOverdue(vm);
  const idFor = (s: string) => `vm-${vm.id}-${s}`;

  const act = async (fn: () => Promise<{ success: boolean; data?: Voicemail }>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (!res.success || !res.data) throw res;
      onChange(res.data);
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`p-4 min-w-0 ${overdue ? 'bg-amber-50' : ''}`}>
      <div className="flex items-start gap-3 min-w-0">
        <VoicemailIcon className={`h-5 w-5 shrink-0 ${vm.status === 'followed_up' ? 'text-neutral-400' : 'text-violet-600'}`} aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              {vm.customer_id && linkCustomer ? (
                <Link to={`/accounts/customers/${vm.customer_id}`} className="text-sm font-semibold text-primary-700 hover:underline break-words">{who}</Link>
              ) : (
                <span className="text-sm font-semibold text-neutral-900 break-words">{who}</span>
              )}
              {vm.matched_kind && <span className="text-xs text-neutral-500">{MATCH_KIND_LABEL[vm.matched_kind]}</span>}
              {overdue && <span className="text-xs font-medium text-amber-800">Waiting over a day</span>}
            </div>
            <p className="text-xs text-neutral-500 break-words">
              {formatDateTimeInTz(vm.received_at, tz)}
              {vm.caller_number && who !== vm.caller_number ? ` · ${vm.caller_number}` : ''}
              {length ? ` · ${length}` : ''}
              {vm.mailbox ? ` · mailbox ${vm.mailbox}${vm.mailbox_user_name ? ` (${vm.mailbox_user_name})` : ''}` : ''}
            </p>
          </div>

          {vm.can_listen ? (
            <>
              {vm.transcript ? (
                <p className="text-sm text-neutral-800 whitespace-pre-wrap break-words rounded-lg bg-neutral-50 border border-neutral-200 p-3">
                  {vm.transcript}
                </p>
              ) : (
                <p className="text-sm text-neutral-500">{vm.removed_at ? 'Deleted after the keeping period.' : 'No transcript yet.'}</p>
              )}
              {vm.has_audio && <AudioPlayback label="Play voicemail" load={() => salesService.voicemailAudio(vm.id)} />}
            </>
          ) : (
            <p className="text-sm text-neutral-600 break-words">
              {vm.has_transcript ? 'Has a transcript. ' : ''}
              A Sales Admin, the mailbox owner or the person it is given to can listen.
            </p>
          )}

          {vm.status === 'followed_up' ? (
            <div className="text-sm text-neutral-600 break-words">
              <CheckCircle2 className="h-4 w-4 inline text-emerald-600 mr-1" aria-hidden />
              Followed up{vm.followed_up_by_name ? ` by ${vm.followed_up_by_name}` : ''}
              {vm.followed_up_at ? `, ${formatDateTimeInTz(vm.followed_up_at, tz)}` : ''}
              {vm.follow_up_note ? `: ${vm.follow_up_note}` : ''}
              {vm.can_act && (
                <Button size="sm" variant="ghost" className="ml-2" disabled={busy} onClick={() => act(() => salesService.reopenVoicemail(vm.id))}>
                  <RotateCcw className="h-4 w-4 mr-1" aria-hidden />Open again
                </Button>
              )}
            </div>
          ) : vm.can_act ? (
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 min-w-0">
              <div className="min-w-0">
                <label htmlFor={idFor('assign')} className="block text-xs font-medium text-neutral-600 mb-1">Given to</label>
                <select
                  id={idFor('assign')}
                  value={vm.assigned_to_user_id ?? ''}
                  disabled={busy}
                  onChange={(e) => act(() => salesService.assignVoicemail(vm.id, e.target.value ? Number(e.target.value) : null))}
                  className="w-full sm:w-56 rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0"
                >
                  <option value="">Nobody yet</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  {vm.assigned_to_user_id && !people.some((p) => p.id === vm.assigned_to_user_id) && (
                    <option value={vm.assigned_to_user_id}>{vm.assigned_to_name || 'Someone'}</option>
                  )}
                </select>
              </div>
              {closing ? (
                <div className="flex flex-col sm:flex-row sm:items-end gap-2 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <label htmlFor={idFor('note')} className="block text-xs font-medium text-neutral-600 mb-1">What was done (optional)</label>
                    <input id={idFor('note')} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Rang back, order placed"
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" isLoading={busy} onClick={async () => { if (await act(() => salesService.followUpVoicemail(vm.id, note.trim()))) setClosing(false); }}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setClosing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setClosing(true)}>
                  <CheckCircle2 className="h-4 w-4 mr-1" aria-hidden />Followed up
                </Button>
              )}
            </div>
          ) : (
            <p className="text-xs text-neutral-500 break-words">
              {vm.assigned_to_name ? `Given to ${vm.assigned_to_name}` : 'Not given to anyone yet'}
            </p>
          )}
          {error && <p className="text-sm text-red-700 break-words">{error}</p>}
        </div>
      </div>
    </li>
  );
}
